import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ChevronDown, ChevronRight, Loader2, Receipt } from 'lucide-react';
import api from '../../utils/api';
import { errText, usePayEngine } from './payRatesKit';
import usePayPermissions from '../../hooks/usePayPermissions';

// Settings > Integrations > Xero > Contractor Bills (owner spec Pay Rates v1.2, 8.1). Only for a company
// on Pay Rates: the Xero account the bill lines are coded to, the tax rate with and without GST (from
// Xero's tax rate list), the bill status and due days, the pay period, and the ABA file details, the
// same fields as the SC module. Saved in the company's Xero integration settings
// (routes/contractorBills.js PUT /settings).

const ABA_FIELDS = [
  ['aba_fi_abbrev', 'Bank abbreviation', 'For example CBA, WBC, NAB, ANZ', 3],
  ['aba_user_name', 'ABA user name', 'As your bank has it, up to 26 characters', 26],
  ['aba_apca_id', 'APCA user ID', '6 digits, from your bank', 6],
  ['aba_description', 'File description', 'Up to 12 characters, for example CONTRACTORS', 12],
  ['aba_bsb', 'Paying account BSB', '000-000', 7],
  ['aba_account_number', 'Paying account number', 'Up to 9 digits', 9],
  ['aba_account_name', 'Paying account name', 'Up to 32 characters', 32],
];

export default function ContractorBillSettings() {
  const engine = usePayEngine();
  const { can } = usePayPermissions();
  const allowed = can('integrations.xero');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [meta, setMeta] = useState(null);
  const [metaError, setMetaError] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const { data } = await api.get('/contractor-bills/settings');
      setForm({ ...data.settings });
    } catch (e) { setError(errText(e, "We couldn't load the contractor bill settings.")); }
    try { const { data } = await api.get('/contractor-bills/xero/meta'); setMeta(data); setMetaError(''); }
    catch (e) { setMeta(null); setMetaError(errText(e, 'Connect Xero to choose the account and tax rates from your Xero lists.')); }
  }, []);
  useEffect(() => { if (open && !form) load(); }, [open, form, load]);

  if (engine !== 'rates' || !allowed) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const body = { ...form };
      delete body.saved;
      const { data } = await api.put('/contractor-bills/settings', body);
      setForm({ ...data.settings });
      toast.success('Contractor bill settings saved');
    } catch (err) { setError(errText(err, 'Could not save the settings.')); } finally { setBusy(false); }
  };

  const select = (id, k, options, placeholder) => {
    const has = options.some((o) => o.value === form[k]);
    return (
      <select id={id} className="input-field" value={form[k] || ''} onChange={(e) => set(k, e.target.value)}>
        <option value="">{placeholder}</option>
        {!has && form[k] ? <option value={form[k]}>{form[k]}</option> : null}
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  };
  const accounts = ((meta && meta.accounts) || []).map((a) => ({ value: a.code, label: `${a.code} ${a.name}` }));
  const taxes = ((meta && meta.taxRates) || []).map((t) => ({ value: t.type, label: `${t.name}${t.rate != null ? ` (${t.rate}%)` : ''}` }));

  return (
    <div className="rounded-xl border border-slate-200 bg-white">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left">
        <span className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-violet-50 text-violet-700"><Receipt size={16} aria-hidden="true" /></span>
          <span>
            <span className="block text-sm font-semibold text-slate-800">Xero: Contractor Bills</span>
            <span className="block text-xs text-slate-500">How contractor bills go to Xero and the ABA payment file. Bills are made in Timesheets &gt; Contractor bills.</span>
          </span>
        </span>
        {open ? <ChevronDown size={16} className="text-slate-400" aria-hidden="true" /> : <ChevronRight size={16} className="text-slate-400" aria-hidden="true" />}
      </button>
      {open && (
        <div className="border-t border-slate-100 px-4 py-4">
          {!form ? (
            error ? <p className="text-[13px] text-rose-700">{error}</p> : <p className="flex items-center gap-2 text-[13px] text-slate-500"><Loader2 size={14} className="animate-spin" aria-hidden="true" />Loading</p>
          ) : (
            <form onSubmit={save} className="space-y-5">
              {metaError && <p className="rounded-lg bg-amber-50 px-3 py-2 text-[12.5px] text-amber-900">{metaError}</p>}
              {meta && meta.needsReconnect && <p className="rounded-lg bg-amber-50 px-3 py-2 text-[12.5px] text-amber-900">Reconnect Xero to let Rostery read your accounts and tax rates.</p>}
              {!form.saved && <p className="rounded-lg bg-slate-50 px-3 py-2 text-[12.5px] text-slate-600">Not saved yet. The ABA details below start from the ones you already use to pay contractors, where there are any.</p>}
              <fieldset className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <legend className="mb-2 text-[12px] font-semibold uppercase tracking-wide text-slate-500">Bill lines</legend>
                <div>
                  <label htmlFor="cbs-account" className="input-label">Account code</label>
                  {accounts.length ? select('cbs-account', 'account_code', accounts, 'Choose an account')
                    : <input id="cbs-account" className="input-field" value={form.account_code || ''} onChange={(e) => set('account_code', e.target.value)} placeholder="For example 478" />}
                </div>
                <div>
                  <label htmlFor="cbs-gst" className="input-label">Tax rate with GST</label>
                  {taxes.length ? select('cbs-gst', 'tax_gst', taxes, 'Choose a tax rate')
                    : <input id="cbs-gst" className="input-field" value={form.tax_gst || ''} onChange={(e) => set('tax_gst', e.target.value)} />}
                  <p className="mt-1 text-[11.5px] text-slate-500">When the contractor&apos;s GST Registered tick is on.</p>
                </div>
                <div>
                  <label htmlFor="cbs-nogst" className="input-label">Tax rate without GST</label>
                  {taxes.length ? select('cbs-nogst', 'tax_nongst', taxes, 'Choose a tax rate')
                    : <input id="cbs-nogst" className="input-field" value={form.tax_nongst || ''} onChange={(e) => set('tax_nongst', e.target.value)} />}
                  <p className="mt-1 text-[11.5px] text-slate-500">When the tick is off.</p>
                </div>
              </fieldset>
              <fieldset className="grid grid-cols-1 gap-4 sm:grid-cols-4">
                <legend className="mb-2 text-[12px] font-semibold uppercase tracking-wide text-slate-500">Bills and pay periods</legend>
                <div>
                  <label htmlFor="cbs-status" className="input-label">Send bills as</label>
                  <select id="cbs-status" className="input-field" value={form.bill_status} onChange={(e) => set('bill_status', e.target.value)}>
                    <option value="DRAFT">Draft</option>
                    <option value="SUBMITTED">Awaiting approval</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="cbs-due" className="input-label">Days until due</label>
                  <input id="cbs-due" type="number" min="0" max="120" className="input-field" value={form.bill_due_days} onChange={(e) => set('bill_due_days', e.target.value === '' ? '' : Number(e.target.value))} />
                  <p className="mt-1 text-[11.5px] text-slate-500">Bills are dated the last day of the pay period.</p>
                </div>
                <div>
                  <label htmlFor="cbs-period" className="input-label">Pay period</label>
                  <select id="cbs-period" className="input-field" value={form.pay_period} onChange={(e) => set('pay_period', e.target.value)}>
                    <option value="weekly">Weekly</option>
                    <option value="fortnightly">Fortnightly</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="cbs-anchor" className="input-label">A pay period starts on</label>
                  <input id="cbs-anchor" type="date" className="input-field" value={form.pay_period_anchor || ''} onChange={(e) => set('pay_period_anchor', e.target.value)} />
                </div>
              </fieldset>
              <fieldset className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <legend className="mb-2 text-[12px] font-semibold uppercase tracking-wide text-slate-500">ABA payment file</legend>
                {ABA_FIELDS.map(([k, label, hint, max]) => (
                  <div key={k}>
                    <label htmlFor={`cbs-${k}`} className="input-label">{label}</label>
                    <input id={`cbs-${k}`} className="input-field" maxLength={max} value={form[k] || ''} onChange={(e) => set(k, e.target.value)} autoComplete="off" />
                    <p className="mt-1 text-[11.5px] text-slate-500">{hint}</p>
                  </div>
                ))}
                <label className="flex items-center gap-2 self-center text-sm text-slate-700">
                  <input type="checkbox" className="rounded border-slate-300 text-primary-600" checked={!!form.aba_balancing} onChange={(e) => set('aba_balancing', e.target.checked)} />
                  Add a balancing record (some banks need it)
                </label>
              </fieldset>
              {error && <p className="text-[13px] text-rose-700" role="alert">{error}</p>}
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Link to="/timesheets?tab=contractor-bills" className="text-[13px] font-medium text-primary-700 hover:underline">Open Contractor bills</Link>
                <button type="submit" className="btn-primary" disabled={busy}>{busy && <Loader2 size={14} className="animate-spin" aria-hidden="true" />}Save settings</button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
